import { httpResource, HttpResourceRequest } from '@angular/common/http'
import { Component, computed, effect, input, model, signal, untracked, ChangeDetectionStrategy } from '@angular/core'
import { toObservable, toSignal } from '@angular/core/rxjs-interop'
import { Field, FormField } from '@angular/forms/signals'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { SubscriptSizing } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { debounceTime, filter } from 'rxjs'
import {
  osmAutocompleteApiKey,
  searchUrl,
  StreetSearchHints,
  streetSearchParams,
  streetSuggestionLabel,
} from '../street-autocomplete.config'
import { StreetAutocompleteResult } from '../street-autocomplete/street-autocomplete.component'

/**
 * Strasse und Hausnummer als zwei Felder nebeneinander, mit Vorschlagsliste am Strassenfeld. Ein
 * Vorschlag fuellt Strasse, Hausnummer, PLZ und Ort.
 *
 * **`houseNumberField` ist Pflicht-Input:** wer die Komponente benutzt, muss das Hausnummernfeld
 * mitgeben, sonst baut die Vorlage nicht. So kann seit 2026-10 kein Formular mehr eine Strasse ohne
 * eigenes Hausnummernfeld erfassen (Backend `docs/claude/adressen.md`). Die Pruefregeln
 * (Pflicht, keine Nummer im Strassenfeld) haengt das Formular selbst ins Schema:
 * `strasseHausnummerRegeln(...)` aus `hausnummer.util`.
 */
@Component({
  selector: 'lib-street-autocomplete-field',
  imports: [FormField, MatAutocompleteModule, MatInputModule, MatIconModule],
  templateUrl: './street-autocomplete-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './street-autocomplete-field.component.scss',
})
export class StreetAutocompleteFieldComponent {
  /** Der Strassenname OHNE Hausnummer. */
  readonly field = input.required<Field<string>>()
  readonly houseNumberField = input.required<Field<string>>()
  readonly postcode = model<string>('')
  readonly city = model<string>('')
  /** 'dynamic' in Formularen, die ihre Abstaende selbst per gap setzen statt ueber die Subscript-Reserve */
  readonly subscriptSizing = input<SubscriptSizing>('fixed')

  /** Strasse samt Nachbarfeldern im Moment der Abfrage, siehe `constructor`. */
  readonly query = signal<{ q: string; hints: StreetSearchHints }>({ q: '', hints: {} })
  readonly suggestions = httpResource<StreetAutocompleteResult[]>(() => {
    const { q, hints } = this.query()
    if (!q) {
      return undefined
    }
    return <HttpResourceRequest>{
      url: searchUrl,
      params: streetSearchParams(q, hints),
      headers: {
        'x-api-key': osmAutocompleteApiKey,
      },
    }
  })

  readonly streetValue = computed(() => this.field()().value())

  private readonly fieldDirty = computed(() => this.field()().dirty())

  private readonly debouncedValue = toSignal(
    toObservable(this.streetValue).pipe(
      debounceTime(250),
      filter(() => this.fieldDirty()),
    ),
  )

  constructor() {
    // Nur das Strassenfeld loest eine Suche aus; Hausnummer, PLZ und Ort werden untracked mitgelesen,
    // sonst feuerte jede Taste in diesen Feldern eine Abfrage, deren Liste niemand sieht.
    effect(() => {
      const q = this.debouncedValue() ?? ''
      const hints = untracked(() => ({ housenumber: this.houseNumberField()().value(), postcode: this.postcode(), city: this.city() }))
      this.query.set({ q, hints })
    })
  }

  readonly label = streetSuggestionLabel

  /**
   * Die Strasse kommt ueber den `[value]` der Option ins Feld. Hausnummer, PLZ und Ort setzen wir
   * hier. Ein Vorschlag ohne Hausnummer laesst eine schon getippte stehen.
   */
  applyAutocompleteSelection(suggestion: StreetAutocompleteResult) {
    const hausnummer = suggestion.housenumber?.trim()
    if (hausnummer) {
      this.houseNumberField()().value.set(hausnummer)
    }
    this.postcode.set(suggestion.postcode ?? '')
    this.city.set(suggestion.city ?? '')
  }
}
